import {
  ACTION_STATUS_LABELS,
  AGENDA_ITEM_KIND_LABELS,
  DECISION_VOTE_LABELS,
  decisionApprovalItemSchema,
  decisionApprovalListSchema,
  formatDate,
  formatDateTime,
  type Decision,
  type DecisionApprovalItem,
} from '@fluxus/shared';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Inbox, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { TabList, tabPanelProps } from '@/components/ui/tabs';
import { ApprovalBadge, ApprovalSummary, VoteDialog } from '@/features/meetings/Approval';
import { ApiError } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

type Tab = 'pendentes' | 'decididas';
const TABS: { key: Tab; label: string }[] = [
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'decididas', label: 'Decididas' },
];
const PAGE_SIZE = '20';
const approvalsKey = (organizationId: string) => ['decision-approvals', organizationId] as const;

const actionLabel = (status: string) =>
  ACTION_STATUS_LABELS[status as keyof typeof ACTION_STATUS_LABELS] ?? status;

/**
 * Aprovações (T-123b, RF-48; DEC-053): as decisões em que a pessoa é aprovadora, pendentes e decididas, com o contexto
 * para decidir; aprovar ou rejeitar com comentário (e, com ação aberta, cancelar ou manter a ação). O link da
 * notificação (?decisao=) abre a decisão no topo.
 */
export function AprovacoesPage() {
  const { organizationId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('aba') === 'decididas' ? 'decididas' : 'pendentes';
  const linked = params.get('decisao');
  const tabsId = useId();
  const queryClient = useQueryClient();
  const [voting, setVoting] = useState<{ item: DecisionApprovalItem; approve: boolean } | null>(null);
  const setParam = (key: string, value: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  const onVote = (item: DecisionApprovalItem, approve: boolean) => setVoting({ item, approve });

  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Aprovações"
        description="Decisões das análises críticas que pedem o seu voto. A decisão só vale quando todos os aprovadores aprovam; uma rejeição basta para rejeitá-la."
      />
      {linked ? (
        <LinkedApproval
          organizationId={organizationId}
          decisionId={linked}
          onVote={onVote}
          onClose={() => setParam('decisao', null)}
        />
      ) : null}
      <div className="mt-6 flex flex-col gap-4">
        <TabList
          id={tabsId}
          label="Aprovações"
          tabs={TABS}
          value={tab}
          onChange={(next) => setParam('aba', next === 'pendentes' ? null : next)}
        />
        {TABS.map((t) => (
          <div key={t.key} {...tabPanelProps(tabsId, t.key)} hidden={t.key !== tab}>
            {t.key === tab ? (
              <ApprovalList
                organizationId={organizationId}
                status={t.key === 'pendentes' ? 'pending' : 'decided'}
                onVote={onVote}
              />
            ) : null}
          </div>
        ))}
      </div>
      <VoteDialog
        organizationId={organizationId}
        projectId={voting?.item.project.id ?? ''}
        decision={voting?.item.decision ?? null}
        approve={voting?.approve ?? true}
        onClose={() => setVoting(null)}
        onVoted={async (d: Decision) => {
          setVoting(null);
          toast.success(
            d.approval.status === 'APPROVED'
              ? 'Decisão aprovada'
              : d.approval.status === 'REJECTED'
                ? 'Decisão rejeitada'
                : 'Voto registrado',
            { description: d.text },
          );
          await queryClient.invalidateQueries({ queryKey: approvalsKey(organizationId) });
        }}
      />
    </PageContainer>
  );
}

/** A decisão do link da notificação, no topo da tela. */
function LinkedApproval({
  organizationId,
  decisionId,
  onVote,
  onClose,
}: {
  organizationId: string;
  decisionId: string;
  onVote: (item: DecisionApprovalItem, approve: boolean) => void;
  onClose: () => void;
}) {
  const { client } = useAuth();
  const one = useQuery({
    queryKey: [...approvalsKey(organizationId), 'one', decisionId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/decision-approvals/${decisionId}`, {
        schema: decisionApprovalItemSchema,
        signal,
      }),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  return (
    <section aria-label="Decisão do aviso" className="mt-6 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-bold">Decisão do aviso</h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X aria-hidden />
          Fechar
        </Button>
      </div>
      {one.isPending ? (
        <LoadingState rows={2} label="Carregando a decisão" />
      ) : one.isError ? (
        <ErrorState
          className="rounded-lg border border-border py-8"
          title={
            one.error instanceof ApiError && one.error.status === 404
              ? 'Esta decisão não está entre as suas aprovações'
              : undefined
          }
          error={one.error}
          onRetry={() => void one.refetch()}
          retrying={one.isFetching}
        />
      ) : (
        <ApprovalCard item={one.data} highlighted onVote={onVote} />
      )}
    </section>
  );
}

function ApprovalList({
  organizationId,
  status,
  onVote,
}: {
  organizationId: string;
  status: 'pending' | 'decided';
  onVote: (item: DecisionApprovalItem, approve: boolean) => void;
}) {
  const { client } = useAuth();
  const list = useInfiniteQuery({
    queryKey: [...approvalsKey(organizationId), 'list', status],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      client.request(`/organizations/${organizationId}/decision-approvals`, {
        query: { status, limit: PAGE_SIZE, ...(pageParam ? { cursor: pageParam } : {}) },
        schema: decisionApprovalListSchema,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor,
  });
  if (list.isPending) {
    return (
      <LoadingState
        rows={3}
        label={status === 'pending' ? 'Carregando as pendentes' : 'Carregando as decididas'}
      />
    );
  }
  if (list.isError) {
    return <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  const items = list.data.pages.flatMap((p) => p.items);
  if (!items.length) {
    return (
      <EmptyState
        className="rounded-lg border border-dashed border-border py-10"
        icon={Inbox}
        title={status === 'pending' ? 'Nenhuma decisão aguardando o seu voto' : 'Nenhuma aprovação decidida'}
        description={
          status === 'pending'
            ? 'Quando quem conduz uma análise crítica pedir a sua aprovação, a decisão aparece aqui e você recebe um aviso.'
            : 'As decisões em que você votou, ou cuja aprovação terminou, aparecem aqui.'
        }
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <ul
        aria-label={status === 'pending' ? 'Decisões aguardando o seu voto' : 'Aprovações decididas'}
        className="flex flex-col gap-3"
      >
        {items.map((item) => (
          <li key={item.decision.id}>
            <ApprovalCard item={item} onVote={onVote} />
          </li>
        ))}
      </ul>
      {list.hasNextPage ? (
        <Button
          variant="outline"
          className="self-start"
          loading={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          Carregar mais
        </Button>
      ) : null}
    </div>
  );
}

/** Uma decisão para aprovar: o que foi decidido, de onde veio, o responsável, o prazo, a ação e os votos. */
function ApprovalCard({
  item,
  highlighted = false,
  onVote,
}: {
  item: DecisionApprovalItem;
  highlighted?: boolean;
  onVote: (item: DecisionApprovalItem, approve: boolean) => void;
}) {
  const titleId = useId();
  const d = item.decision;
  const canVote = item.mine.vote === 'PENDING' && d.approval.status === 'PENDING';
  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col gap-3 rounded-lg border bg-surface p-4 text-sm',
        highlighted ? 'border-brand' : 'border-border',
      )}
    >
      <div className="flex flex-col gap-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span>{item.project.name}</span>
          <span>
            {item.meeting.title} · {formatDateTime(item.meeting.startsAt)}
          </span>
        </p>
        <h3 id={titleId} className="font-bold break-words">
          {d.text}
        </h3>
        {item.item ? (
          <p className="text-xs text-muted-foreground break-words">
            {AGENDA_ITEM_KIND_LABELS[item.item.kind]}: {item.item.title}
          </p>
        ) : null}
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Registrada por</dt>
        <dd>
          {d.createdByName} em {formatDateTime(d.createdAt)}
        </dd>
        <dt className="text-muted-foreground">Responsável</dt>
        <dd>{d.responsible?.name ?? 'não definido'}</dd>
        <dt className="text-muted-foreground">Prazo</dt>
        <dd>{d.dueDate ? formatDate(d.dueDate) : 'sem prazo'}</dd>
        <dt className="text-muted-foreground">Ação</dt>
        <dd className="break-words">
          {d.action ? `${d.action.title} (${actionLabel(d.action.status)})` : 'sem ação no plano'}
        </dd>
        <dt className="text-muted-foreground">Seu voto</dt>
        <dd>
          {item.mine.vote === 'PENDING' ? (
            d.approval.status === 'PENDING' ? (
              'aguardando'
            ) : (
              'não precisou votar: a aprovação terminou'
            )
          ) : (
            <>
              {DECISION_VOTE_LABELS[item.mine.vote].toLowerCase()}
              {item.mine.decidedAt ? ` em ${formatDateTime(item.mine.decidedAt)}` : ''}
            </>
          )}
        </dd>
      </dl>
      <div className="rounded-md bg-muted/50 px-3 py-2">
        {d.approval.status === 'NONE' ? <ApprovalBadge status="NONE" /> : <ApprovalSummary d={d} />}
      </div>
      {canVote ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            className="w-full sm:w-auto"
            onClick={() => onVote(item, true)}
            aria-label={`Aprovar a decisão ${d.text}`}
          >
            <Check aria-hidden />
            Aprovar
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onVote(item, false)}
            aria-label={`Rejeitar a decisão ${d.text}`}
          >
            <X aria-hidden />
            Rejeitar
          </Button>
        </div>
      ) : null}
    </article>
  );
}
